import styled, { DefaultTheme, useTheme } from 'styled-components';
import { SmallBadge } from '../../elements/Badge';
import { PSmall } from '../../elements/Text';
import { SmallTooltip } from '../../Tooltip';
import {
  CompanyResponse,
  LearningPlanModule,
  LearningPlanProject,
} from '../../../api/admin';
import dayjs from 'dayjs';
import { LEARNING_PLAN_UNIT_TYPE } from '../../LearningPlan/LearningPlanUnit';
import { TOPIC_TO_COLOR } from '../../../theme';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  height: 100%;
  /* width: 100%; */
`;

const BadgeWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: nowrap;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

// const BadgeIcon = styled.div`
//   width: 16px;
//   height: 16px;

//   svg {
//     width: 100%;
//     height: 100%;
//   }
// `;

const BadgeI = styled.i<{ color: string }>`
  font-size: 15px;
  margin: 0;
  padding: 0;
  color: ${(props) => props.color};
`;

const BadgeStatusIcon = ({ src, color }: { src: string; color: string }) => {
  return <BadgeI className={src} color={color} />;
};

const BadgeText = styled(PSmall)`
  text-align: center;
  margin-top: 0;
  padding-bottom: 0;
`;

// const TopicIconSvg = ({
//   topicIcon,
//   unitId,
//   learnerId,
//   topicColor,
// }: {
//   topicIcon: string;
//   unitId: string;
//   learnerId: number;
//   topicColor?: string;
// }) => {
//   const theme = useTheme();
//   return (
//     <BadgeIcon>
//       <svg width="100%" height="100%">
//         <defs>
//           <mask id={`icon-mask-${unitId}-${learnerId}`}>
//             <image href={topicIcon} width="100%" height="100%" />
//           </mask>
//         </defs>
//         <rect
//           width="100%"
//           height="100%"
//           fill={topicColor ?? theme.colors.buttonTextColor}
//           mask={`url(#icon-mask-${unitId}-${learnerId})`}
//         />
//       </svg>
//     </BadgeIcon>
//   );
// };

export default function LearningPlanCell({
  learningPlan,
  learnerId,
}: {
  learnerId: number;
  learningPlan: CompanyResponse['learners'][number]['learningPlan'];
}) {
  const theme = useTheme();

  if ((learningPlan ?? []).length === 0) {
    return (
      <Wrapper>
        <span>-</span>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {(learningPlan ?? []).map((item) => {
        const {
          unitId,
          tooltipId,
          colors,
          tooltipCopy,
          badgeCopy,
          statusIconClass,
        } = getLearningPlanItemStyles({
          item,
          learnerId,
          theme,
        });

        return (
          <BadgeWrapper key={unitId} data-tooltip-id={tooltipId}>
            <SmallBadge
              colors={colors}
              style={{
                width: 'max-content',
              }}
            >
              <Row>
                <BadgeStatusIcon
                  src={statusIconClass}
                  color={colors.highlightColor}
                />
                {/* <TopicIconSvg
                  topicIcon={item.topicIconUrl}
                  unitId={unitId}
                  learnerId={learnerId}
                  topicColor={colors.highlightColor}
                /> */}
                <BadgeText>{badgeCopy}</BadgeText>
              </Row>
            </SmallBadge>

            <SmallTooltip anchorSelect={`[data-tooltip-id="${tooltipId}"]`}>
              {tooltipCopy}
            </SmallTooltip>
          </BadgeWrapper>
        );
      })}
    </Wrapper>
  );
}

function getStatusIconClass({
  item,
}: {
  item: LearningPlanProject | LearningPlanModule;
}): string {
  if (item.type === LEARNING_PLAN_UNIT_TYPE.PROJECT) {
    if (item.submittedExercisesCount === item.exercisesCount) {
      return 'fa-solid fa-check';
    }
    if ((item?.submittedExercisesCount ?? 0) > 0) {
      return 'fa-solid fa-circle-quarter-stroke';
    }

    return 'fa-solid fa-play';
  }

  if (!item.didOnboard && !item.wasInvited) {
    return 'fa-solid fa-lock';
  }

  if (!item.didOnboard && item.wasInvited) {
    return 'fa-solid fa-envelope';
  }

  if (item.coreSubmittedLessonsCount === item.coreLessonsCount) {
    return 'fa-solid fa-check';
  }

  if ((item.coreSubmittedLessonsCount ?? 0) > 0) {
    return 'fa-solid fa-circle-quarter-stroke';
  }

  return 'fa-solid fa-play';
}

function getBadgeColors({
  item,
  theme,
}: {
  item: LearningPlanProject | LearningPlanModule;
  theme: DefaultTheme;
}) {
  const topic = item.topic;
  const topicColor =
    TOPIC_TO_COLOR?.[topic as keyof typeof TOPIC_TO_COLOR] ??
    theme.colors.bgDefault;

  return {
    textColor: theme.colors.textPrimary,
    backgroundColor: topicColor,
    highlightColor: theme.colors.textPrimary,
  };
}

function getTooltip({
  item,
}: {
  item: LearningPlanProject | LearningPlanModule;
}): React.ReactNode {
  if (item.type === LEARNING_PLAN_UNIT_TYPE.PROJECT) {
    const status = (() => {
      if (item.submittedExercisesCount === item.exercisesCount) {
        return 'Completed';
      }
      if ((item?.submittedExercisesCount ?? 0) > 0) {
        return 'In Progress';
      }
      return 'Not Started';
    })();

    return (
      <span>
        <b>{item.topic.toUpperCase()} Project</b>
        <br />
        {status}
        <br />
        {`${item.submittedExercisesCount}/${item.exercisesCount} exercises
        submitted`}
      </span>
    );
  }

  if (item.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE) {
    let status;
    if (item.didOnboard) {
      if (item.coreSubmittedLessonsCount === item.coreLessonsCount) {
        status = 'Onboarded / Complete';
      } else if (item.coreSubmittedLessonsCount === 0) {
        status = 'Onboarded / Not Started';
      } else {
        status = 'Onboarded / In Progress';
      }
    } else if (item.wasInvited) {
      status = 'Invited to Onboard';
    } else {
      status = 'Locked / Needs Invite';
    }

    const coreProgress = item.didOnboard
      ? `${item.coreSubmittedLessonsCount}/${item.coreLessonsCount} CORE actitivites submitted`
      : null;
    const stretchProgress = item.didOnboard
      ? `${item.stretchSubmittedLessonsCount}/${item.stretchLessonsCount} STRETCH actitivites submitted`
      : null;
    const cycleProgress =
      item.didOnboard || item.wasInvited
        ? `${dayjs(item.latestCohortStartDate).format('MMM D, YYYY')} - ${dayjs(
            item.latestCohortEndDate
          ).format('MMM D, YYYY')}`
        : null;

    return (
      <span>
        <b>CONTENT MODULE</b>
        <br />
        {status}
        <br />
        {item.didOnboard && `${coreProgress}`}
        {item.didOnboard && <br />}
        {item.didOnboard && `${stretchProgress}`}
        {(item.didOnboard || item.wasInvited) && (
          <>
            <br />
            <br />
            <b>Latest Cohort</b>
            <br />
            {cycleProgress}
          </>
        )}
      </span>
    );
  }
}

function getLearningPlanItemStyles({
  item,
  learnerId,
  theme,
}: {
  item: LearningPlanProject | LearningPlanModule;
  learnerId: number;
  theme: DefaultTheme;
}) {
  const unitId =
    item.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
      ? `${item.content.id}-${item.type}-${learnerId}`
      : `${item.projectId}-${item.type}-${learnerId}`;

  return {
    unitId,
    tooltipId: `learning-plan-${unitId}`,
    colors: getBadgeColors({ item, theme }),
    tooltipCopy: getTooltip({ item }),
    badgeCopy:
      item.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE
        ? item.content.title
        : item.title,
    statusIconClass: getStatusIconClass({ item }),
  };
}
